import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, AlertCircle, Mail, Lock, User as UserIcon } from 'lucide-react';
import { useAuth } from '../services/AuthContext';
import { useAuthModal } from '../context/AuthModalContext';
import { useGoogleLogin } from '@react-oauth/google';
import { useToast } from '../services/ToastContext';
import api from '../services/api';

const AuthModal = () => {
    const { view, close, openLogin, openRegister } = useAuthModal();
    const { login, signup } = useAuth();
    const { showToast } = useToast();

    // Shared State
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Form State
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');

    useEffect(() => {
        if (!view) {
            setError('');
            setLoading(false);
            setEmail('');
            setPassword('');
            setFirstName('');
            setLastName('');
        }
    }, [view]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email.trim() || !password.trim()) {
            setError("Please fill in all required fields.");
            return;
        }

        if (view === "register" && (!firstName.trim() || !lastName.trim())) {
            setError("Please enter your full name.");
            return;
        }
        
        setLoading(true);
        try {
            if (view === "login") {
                await login(email, password);
                showToast('Welcome Back!', 'success');
                close();
            } else {
                await signup(email, password, { first_name: firstName, last_name: lastName });
                showToast('Verification email sent! Please check your inbox.', 'info');
                close();
            }
        } catch (err: any) {
            setError(err.message || "Authentication failed. Please check your credentials.");
        } finally {
            setLoading(false);
        }
    };

    const googleLogin = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            setLoading(true);
            try {
                const res = await api.post('/users/social-auth', {
                    token: tokenResponse.access_token,
                    provider: 'google'
                });
                // Note: Social auth usually sets a token directly or triggers a reload in our setup
                if (res.data.access_token) {
                    localStorage.setItem('laqy_auth_token', res.data.access_token);
                    window.location.reload();
                }
                showToast('Successfully logged in!', 'success');
                close();
            } catch (err: any) {
                setError(err.message || "Google Auth Error");
            } finally {
                setLoading(false);
            }
        },
    });

    const isLogin = view === 'login';

    return (
        <AnimatePresence>
            {view && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={close}
                        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
                    />

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 10 }}
                        className="relative w-full max-w-[420px] bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col border border-slate-200"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-slate-100">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2">Secure Node Access</span>
                            <button onClick={close} className="p-2 rounded-full hover:bg-slate-50 transition-colors text-slate-400 hover:text-slate-800">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="px-8 py-8">
                            {/* Logo & Greeting */}
                            <div className="flex flex-col items-center mb-8">
                                <img src="/laqyicon.svg" alt="Laqy Estate" className="h-12 w-auto mb-4" />
                                <h2 className="text-2xl font-black text-slate-900 text-center tracking-tight">
                                    {isLogin ? "Welcome Back" : "Secure Registration"}
                                </h2>
                                <p className="text-sm text-slate-500 text-center mt-2 font-medium">
                                    {isLogin 
                                        ? "Enter your credentials to access your session." 
                                        : "Join the verified network of professional real estate."}
                                </p>
                            </div>

                            {/* Form Area */}
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {!isLogin && (
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-bold text-slate-500 ml-1 uppercase">First Name</label>
                                            <div className="relative">
                                                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                                <input
                                                    type="text"
                                                    required
                                                    value={firstName}
                                                    onChange={(e) => setFirstName(e.target.value)}
                                                    placeholder="John"
                                                    className="w-full bg-slate-50 border border-slate-200 rounded-md pl-10 pr-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:border-slate-400 transition-all focus:ring-4 focus:ring-slate-100"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-bold text-slate-500 ml-1 uppercase">Last Name</label>
                                            <div className="relative">
                                                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                                <input
                                                    type="text"
                                                    required
                                                    value={lastName}
                                                    onChange={(e) => setLastName(e.target.value)}
                                                    placeholder="Doe"
                                                    className="w-full bg-slate-50 border border-slate-200 rounded-md pl-10 pr-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:border-slate-400 transition-all focus:ring-4 focus:ring-slate-100"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-slate-500 ml-1 uppercase">Email Address</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="name@company.com"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-md pl-10 pr-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:border-slate-400 transition-all focus:ring-4 focus:ring-slate-100"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-slate-500 ml-1 uppercase">Authentication Key</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="password"
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-md pl-10 pr-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:border-slate-400 transition-all focus:ring-4 focus:ring-slate-100"
                                        />
                                    </div>
                                </div>

                                {error && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="p-3 bg-red-50 border border-red-100 text-red-600 text-[11px] font-black rounded-md flex items-center gap-2"
                                    >
                                        <AlertCircle className="w-4 h-4 shrink-0" /> {error.toUpperCase()}
                                    </motion.div>
                                )}

                                <button 
                                    disabled={loading} 
                                    type="submit" 
                                    className="w-full bg-slate-900 text-white font-black py-4 rounded-md flex items-center justify-center gap-3 mt-6 transition-all hover:bg-slate-800 disabled:opacity-50 active:scale-[0.98]"
                                >
                                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (isLogin ? "INITIALIZE SESSION" : "REGISTER IDENTITY")}
                                </button>

                                <div className="flex items-center py-6">
                                    <div className="flex-1 h-px bg-slate-100" />
                                    <span className="px-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Global Protocol</span>
                                    <div className="flex-1 h-px bg-slate-100" />
                                </div>

                                <div className="grid grid-cols-2 gap-3 mb-6">
                                    <button type="button" onClick={() => googleLogin()} className="flex items-center justify-center gap-2 py-3 border border-slate-200 rounded-md text-xs font-black text-slate-700 hover:bg-slate-50 transition-colors uppercase">
                                        <img src="https://www.gstatic.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" className="w-4 h-4" alt="Google" /> Google
                                    </button>
                                    <button type="button" className="flex items-center justify-center gap-2 py-3 bg-black text-white rounded-md text-xs font-black hover:bg-slate-800 transition-colors uppercase">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg> Apple ID
                                    </button>
                                </div>

                                <div className="text-center pt-2">
                                    <p className="text-xs text-slate-500 font-bold uppercase tracking-tight">
                                        {isLogin ? "New user?" : "Existing user?"}
                                        <button type="button" onClick={() => isLogin ? openRegister() : openLogin()} className="text-slate-900 font-black hover:underline ml-2">
                                            {isLogin ? "REQUEST ACCESS" : "AUTHORIZE NOW"}
                                        </button>
                                    </p>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default AuthModal;
